import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Sparkles, Loader2, AlertCircle, Plus, X, CheckCircle2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  useActionItems,
  useGenerateActionItems,
  useIgnoreActionItem,
  useAddToKanban,
} from '../../../hooks/useActionItems'
import { PromptSettingsDialog } from './PromptSettingsDialog'

interface ActionItemsSectionProps {
  sessionId: string
}

export function ActionItemsSection({ sessionId }: ActionItemsSectionProps) {
  const { data, isLoading, error } = useActionItems(sessionId)
  const generateMutation = useGenerateActionItems(sessionId)
  const ignoreItemMutation = useIgnoreActionItem()
  const addToKanbanMutation = useAddToKanban()
  const { toast } = useToast()

  const handleGenerate = () => {
    generateMutation.mutate()
  }

  const handleIgnore = (itemId: number) => {
    ignoreItemMutation.mutate({ itemId, sessionId })
  }

  const handleAddToKanban = (itemId: number, title: string) => {
    addToKanbanMutation.mutate(
      { itemId, sessionId },
      {
        onSuccess: () => {
          toast({
            title: 'Added to Kanban',
            description: `"${title}" has been added to the Backlog column.`,
          })
          // Refresh action items list to update in_kanban flags
        },
        onError: (error: any) => {
          toast({
            title: 'Error',
            description: error.response?.data?.error || 'Failed to add to Kanban board',
            variant: 'destructive',
          })
        },
      }
    )
  }

  const visibleItems = data?.items.filter((item) => !item.is_ignored) || []

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Action Items</CardTitle>
          <div className="flex items-center gap-1">
            <PromptSettingsDialog />
            <Button
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
              size="sm"
              variant="outline"
            >
              {generateMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-1" />
              )}
              {generateMutation.isPending ? 'Generating...' : 'Generate'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Error State */}
        {(error || generateMutation.isError) && (
          <Alert variant="destructive" className="mb-3">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to {error ? 'load' : 'generate'} action items. Please try again.
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && visibleItems.length === 0 && !generateMutation.isPending && (
          <div className="text-center text-muted-foreground italic py-4">
            No action items yet.
          </div>
        )}

        {/* Action Items List */}
        {!isLoading && visibleItems.length > 0 && (
          <ul className="space-y-3">
            {visibleItems.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
              >
                {/* Item Title */}
                <div className="text-sm flex-1">{item.title}</div>

                {/* Controls - Icon Buttons */}
                <div className="flex items-center gap-1 ml-4">
                  <TooltipProvider>
                    {/* Show "In Kanban" badge or "Add to Backlog" button */}
                    {item.in_kanban ? (
                      <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
                        <CheckCircle2 className="h-3 w-3" />
                        In Kanban
                      </Badge>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            className="h-8 w-8 bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleAddToKanban(item.id, item.title)}
                            disabled={addToKanbanMutation.isPending}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Add to Backlog</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Ignore Button */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleIgnore(item.id)}
                          disabled={ignoreItemMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Ignore</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
